import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  Checkbox,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import axios from "axios";
import { ocrTypes, navPath, navSearch } from "../../constants";
import { HV_THEME, HVHeader, HVButton, Card as HVCard, Icons } from "../shared/DesignSystem";
import { useSnackbar } from "notistack";
import { CopyBlock, dracula } from "react-code-blocks";
import Editor, { DiffEditor } from "@monaco-editor/react";
import { getCurrentInferenceDomain } from "../../constants";
import { useParams } from "react-router-dom";

function Classification(props) {
  const { groupId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState("");
  const [classificationType, setClassificationType] = useState("");
  const [currentClassificationType, setCurrentClassificationType] = useState("");
  const [tempClassificationType, setTempClassificationType] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openKeywordConfig, setOpenKeywordConfig] = useState(false);
  const [openCompareKeywordConfig, setOpenCompareKeywordConfig] = useState(false);
  const [ocr, setOcr] = useState('google');
  const [tempOcr, setTempOcr] = useState('google');
  const [translate, setTranslate] = useState(false);
  const [tempTranslate, setTempTranslate] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [tempSorting, setTempSorting] = useState(false);
  const [keywordClassifierConfig, setKeywordClassifierConfig] = useState("");
  const [groupName, setGroupName] = useState("");
  const [tempKeywordClassifierConfig, setTempKeywordClassifierConfig] = useState("");

  const getStatus = async () => {
    try {
      const response = await axios.post("/api/groups/status", { groupId: groupId });
      setStatus(response.data.status);
      setGroupName(response.data.name || groupId);
      setClassificationType(response.data.classificationType);
      setTempClassificationType(response.data.classificationType);
      setOcr(response.data.ocr);
      setTempOcr(response.data.ocr);
      setTranslate(response.data.translate);
      setTempTranslate(response.data.translate);
      setSorting(response.data.sorting);
      setTempSorting(response.data.sorting);
      setKeywordClassifierConfig(JSON.stringify(response.data.keywordClassifierConfig, null, 2));
      setTempKeywordClassifierConfig(JSON.stringify(response.data.keywordClassifierConfig, null, 2));

      const projectResponse = response.data.projects;
      projectResponse.push({ _id: 'OTHER', name: 'OTHER' })
      setProjects(projectResponse);
      if (response.data.classificationType === 'image') {
        setCurrentClassificationType('image')
      } else if (response.data.classificationType === 'keyword') {
        setCurrentClassificationType('keyword');
      } else {
        setCurrentClassificationType('text');
      }
    } catch (e) {
      enqueueSnackbar('Error fetching group status', {
        variant: "error",
      });
    };
  }

  const handleDataCreation = async () => {
    try {
      const data = {
        groupId: groupId,
        classificationType: currentClassificationType,
      }
      await axios.post("/api/classification/create", data);
      enqueueSnackbar('Database creation started', {
        variant: "success",
      });
    } catch (e) {
      enqueueSnackbar('Error creating classification database', {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (groupId) {
      getStatus();
    }
  }, [groupId]);

  const handleGoBack = () => {
    props.history.push(navPath(`/group/${groupId}`));
  };

  const handleTempClassificationType = (e) => {
    setTempClassificationType(e.target.value);
  }

  const handleOcr = (e) => {
    setTempOcr(e.target.value);
  }

  const handleTranslate = (e) => {
    setTempTranslate(e.target.checked)
  }

  const handleSorting = (e) => {
    setTempSorting(e.target.checked)
  }

  const handleOpenDialog = () => {
    setOpenDialog(true);
  }

  const handleOpenKeywordConfig = () => {
    setOpenKeywordConfig(true);
  }

  const handleCloseDialog = () => {
    setTempClassificationType(classificationType);
    setTempOcr(ocr);
    setTempTranslate(translate);
    setTempSorting(sorting);
    setOpenDialog(false);
  }

  const handleCloseKeywordConfig = () => {
    setOpenKeywordConfig(false);
    setOpenCompareKeywordConfig(false);
  }

  function handleEditorChange(value, event) {
    setTempKeywordClassifierConfig(value);
  }

  const handleNext = () => {
    setOpenKeywordConfig(false);
    setOpenCompareKeywordConfig(true);
  }

  const handleBack = () => {
    setOpenCompareKeywordConfig(false);
    setOpenKeywordConfig(true);
  }

  const handleSave = async () => {
    try {
      const data = {
        classificationType: tempClassificationType,
        ocr: tempOcr,
        translate: tempTranslate,
        sorting: tempSorting,
        keywordClassifierConfig: tempKeywordClassifierConfig,
        projects: projects.map(project => project._id),
      }
      await axios.put(`/api/groups/${groupId}`, data);
      setOpenDialog(false);
      setOpenCompareKeywordConfig(false);
      getStatus();
      enqueueSnackbar('Classification config updated', {
        variant: "success",
      });
    } catch (err) {
      if (err?.response?.status === 400) {
        enqueueSnackbar(err?.response?.data, {
          variant: "error",
        });
      } else {
        enqueueSnackbar('Error updating classification config', {
          variant: "error",
        });
      }
    }
  }

  const handleOpenProject = (id, name, projectType, ocr) => {
    props.history.push(navPath(`/classification/project/${groupId}/${id}`));
  };

  const handleClassifierTest = () => {
    props.history.push(navPath(`/classification/test/${groupId}`));
  };

  const code = `curl --location --request POST 'https://${getCurrentInferenceDomain()}/v1/readClass' \\
    --header 'appid: xxxxxx' \\
    --header 'appkey: xxxxxxxxxxxxxxxxxxxx' \\
    --form 'image=@"image.png"' \\
    --form 'classification='classificationType' \\
    --form 'group="${groupId}"'`;

  return (
    <div style={{ background: '#fafafa', minHeight: '100vh' }}>
      <HVHeader tab="groups" showTabs={false} role={{}} env="STAGING" onLogout={() => props.history.push('/login')} />
      <div style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '14px 40px' }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Button variant="outlined" size="small" onClick={handleGoBack} startIcon={<ArrowBackIcon />}>Back</Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#666' }}>
              <span>Groups</span>
              <span>/</span>
              <span style={{ cursor: 'pointer' }} onClick={handleGoBack}>{groupName || groupId}</span>
              <span>/</span>
              <span style={{ color: '#000', fontWeight: 600 }}>Classification</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {currentClassificationType === 'keyword' && (
              <HVButton variant="primary" onClick={handleOpenKeywordConfig}>Keyword Config</HVButton>
            )}
            <HVButton variant="primary" onClick={handleOpenDialog} icon={<Icons.cog size={14} />}>Set Config</HVButton>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 40px', maxWidth: 1440, margin: '0 auto' }}>
        {currentClassificationType && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <span style={{ fontSize: 11, letterSpacing: '0.12em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase' }}>Classification Type</span>
            <span style={{ fontSize: 13, fontWeight: 600, background: '#f1f1f1', borderRadius: 6, padding: '2px 10px' }}>{currentClassificationType}</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 28 }}>
          {projects.length === 0
            ? <div style={{ color: HV_THEME.mute, fontSize: 14 }}>No existing projects</div>
            : projects.map((pro) => (
              <HVCard key={pro._id} pad={0} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: 16, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{pro.name}</div>
                  <div style={{ fontSize: 11, color: HV_THEME.mute, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer', wordBreak: 'break-all' }}
                    onClick={() => { navigator.clipboard.writeText(pro._id); enqueueSnackbar(`${pro._id} copied`, { variant: 'success', autoHideDuration: 800 }); }}>
                    {pro._id} <Icons.copy size={10} />
                  </div>
                </div>
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 12px', background: '#fafafa' }}>
                  <HVButton size="sm" variant="secondary" style={{ width: '100%' }} onClick={() => handleOpenProject(pro._id, pro.name, pro.projectType, pro.ocr)}>Open</HVButton>
                </div>
              </HVCard>
            ))
          }
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <HVButton variant="primary" onClick={handleDataCreation}>Create Database</HVButton>
          {status === 'creating_database' && (
            <span style={{ fontSize: 13, color: HV_THEME.mute }}>Creating database…</span>
          )}
          {status === 'database_created' && (
            <HVButton variant="secondary" onClick={handleClassifierTest}>Test Classifier</HVButton>
          )}
        </div>

        {status === 'database_created' && (
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 12, color: HV_THEME.mute, marginBottom: 10 }}>Or test using curl:</div>
            <div style={{ width: '70%' }}>
              <CopyBlock text={code} language="javascript" theme={dracula} />
            </div>
          </div>
        )}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="config"
      >
        <DialogContent>
          <h2>Update Classification Config</h2>
          <FormControl variant="outlined" style={{ marginTop: 20 }}>
            <InputLabel id="select-classification">Classification</InputLabel>
            <Select
              labelId="select-classification"
              id="select-classification-id"
              value={tempClassificationType}
              onChange={handleTempClassificationType}
              label="Classification"
              style={{ height: 50, width: 250 }}
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="keyword">Keyword</MenuItem>
              <MenuItem value="image">Image</MenuItem>
            </Select>
          </FormControl>
          <br />
          <FormControl variant="outlined" style={{ marginTop: 20 }}>
            <InputLabel id="select-ocr">OCR Type</InputLabel>
            <Select
              labelId="select-ocr"
              id="select-ocr-id"
              value={tempOcr}
              onChange={handleOcr}
              label="OCR Type"
              style={{ height: 50, width: 150 }}
            >
              {Object.keys(ocrTypes).map((ocrType) => (
                <MenuItem value={ocrType}>{ocrTypes[ocrType]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <br />
          <div>
            <Checkbox
              checked={tempTranslate}
              onChange={handleTranslate}
              inputProps={{ "aria-label": "primary checkbox" }}
              required
            />
            <span>Enable Translation</span>
          </div>
          <div>
            <Checkbox
              checked={tempSorting}
              onChange={handleSorting}
              inputProps={{ "aria-label": "primary checkbox" }}
              required
            />
            <span>Enable Sorting</span>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openKeywordConfig}
        onClose={handleCloseKeywordConfig}
        aria-labelledby="config"
        maxWidth="lg"
      >
        <DialogContent>
          <Editor
            height="90vh"
            width={window.innerWidth - 200}
            defaultLanguage="json"
            theme="vs-dark"
            defaultValue={tempKeywordClassifierConfig}
            onChange={handleEditorChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseKeywordConfig}
            variant="contained"
            color="secondary"
          >
            Close
          </Button>
          <Button
            onClick={handleNext}
            variant="contained"
            color="default"
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCompareKeywordConfig}
        onClose={handleCloseKeywordConfig}
        aria-labelledby="config"
        maxWidth="lg"
      >
        <DialogContent>
          <DiffEditor
            height="90vh"
            width={window.innerWidth - 200}
            defaultLanguage="json"
            theme="vs-dark"
            original={keywordClassifierConfig}
            modified={tempKeywordClassifierConfig}
            options={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseKeywordConfig}
            variant="contained"
            color="secondary"
          >
            Close
          </Button>
          <Button
            onClick={handleBack}
            variant="contained"
            color="primary"
          >
            Back
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="default"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </div>
  );
}

export default Classification;
